import request from "../../../request";

interface AgrConditionData {
  [propertyTicker: string]: {
    childPropertyTickers?: string[];
    [key: string]: unknown;
  };
}

interface CloneEntity {
  childPropertyTickers: string[];
  [key: string]: unknown;
}

interface ExcelResult {
  data: Blob;
  filename: string;
}

function createClient(baseURL: string) {
  return request.create({ baseURL });
}

export async function fetchAgrConditionData(
  baseURL: string,
  propertyTicker: string,
  conditionTicker: string,
  signal?: AbortSignal
): Promise<AgrConditionData> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(
    `/api/backoffice/aggregator/${propertyTicker}/conditions/${conditionTicker}`,
    { signal }
  );
  return response.data || {};
}

export async function saveAgrCondition(
  baseURL: string,
  propertyTicker: string,
  conditionTicker: string,
  chainBody: Record<string, unknown>,
  isEdit: boolean
): Promise<void> {
  const httpClient = createClient(baseURL);
  if (isEdit) {
    await httpClient.put(
      `/api/backoffice/aggregator/${propertyTicker}/conditions/${conditionTicker}`,
      chainBody
    );
  } else {
    await httpClient.post(`/api/backoffice/aggregator/${propertyTicker}/conditions`, chainBody);
  }
}

export async function deleteAgrCondition(
  baseURL: string,
  propertyTicker: string,
  conditionTicker: string
): Promise<void> {
  const httpClient = createClient(baseURL);
  await httpClient.delete(
    `/api/backoffice/aggregator/${propertyTicker}/conditions/${conditionTicker}`
  );
}

export async function cloneAgrCondition(
  baseURL: string,
  propertyTicker: string,
  conditionTicker: string
): Promise<CloneEntity> {
  const httpClient = createClient(baseURL);
  const [individualRes, agrRes] = await Promise.all([
    httpClient.get(`/api/backoffice/${propertyTicker}/conditions/${conditionTicker}`),
    httpClient.get(`/api/backoffice/aggregator/${propertyTicker}/conditions/${conditionTicker}`),
  ]);
  const flatEntity = individualRes.data;
  const parentEntry = agrRes.data?.[propertyTicker];
  const childPropertyTickers: string[] = parentEntry?.childPropertyTickers || [];
  const cloneEntity = { ...flatEntity, childPropertyTickers };
  delete cloneEntity.id;
  delete cloneEntity.ticker;
  return cloneEntity;
}

export async function downloadConditionsExcel(
  baseURL: string,
  propertyTicker: string
): Promise<ExcelResult> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(`/api/backoffice/${propertyTicker}/conditionrates/xlsx`, {
    responseType: "blob",
  });
  const contentDisposition: string = response.headers["content-disposition"] || "";
  const filenamePart = contentDisposition.split(";")[1];
  const filename = filenamePart
    ? filenamePart.trim().split("=")[1].replace(/"/g, "")
    : `conditions-${propertyTicker}-${Date.now()}.xlsx`;
  return { data: response.data, filename };
}
